import * as constants from "./core/constants.js";

// Canonical playstyle definitions, shared by the client (the lobby chip + preview
// and the setup panel's one-click flavors) and the edge-server (which assigns a
// playstyle to each public box per cycle and builds its matchConfig). Keeping the
// GAMEPLAY here (power-up set + spawn rate) means the chip a player sees always
// matches the match the server actually runs. Presentation (icon/color) stays in
// the client's presets.ts, keyed by name.
//
//  - enabled:         power-up keys ON ("all" = every power-up; the rest are disabled).
//  - spawnMultiplier: power-up spawn rate as a multiple of POWER_UP_SPAWN_CHANCE
//                     (1 = Normal, 2 = Frequent).
export const PLAYSTYLES = [
    { name: "Classic", enabled: "all", spawnMultiplier: 1 },
    { name: "Identity crisis", enabled: ["switcharoonie", "twin", "key_switch"], spawnMultiplier: 1 },
    { name: "Sabotage", enabled: ["speed", "fat", "slow_turn", "key_switch", "drunk", "tron_turn"], spawnMultiplier: 2 },
    { name: "Drink and drive", enabled: ["drunk"], spawnMultiplier: 2 },
    { name: "Fatboy Slim", enabled: ["fat", "slim"], spawnMultiplier: 1 },
];

export const PLAYSTYLE_NAMES = PLAYSTYLES.map((p) => p.name);

// Every spawnable power-up key (the full set the engine spawns from), so a
// playstyle's "disabled" list is exactly the complement of its enabled set.
export const ALL_POWERUP_KEYS = Object.keys(constants.powerUpDefinitions);

export function getPlaystyle(name) {
    return PLAYSTYLES.find((p) => p.name === name);
}

// The disabledPowerUps list a playstyle implies (for matchConfig / preview).
export function playstyleDisabledPowerUps(name) {
    const ps = getPlaystyle(name);
    if (!ps || ps.enabled === "all") {
        return [];
    }
    const on = new Set(ps.enabled);
    return ALL_POWERUP_KEYS.filter((k) => !on.has(k));
}

// The absolute spawn chance a playstyle implies (undefined name -> core default).
export function playstyleSpawnChance(name) {
    const ps = getPlaystyle(name);
    return constants.POWER_UP_SPAWN_CHANCE * (ps ? ps.spawnMultiplier : 1);
}

// ---- round modes (Play Now and Royale: a new mode every round) ----
// The playstyles above plus "no power-ups at all". One list for the host (which
// rolls a mode per round and builds its rules) and the client (which announces
// the next round's mode by name).
export const NO_POWERUPS_MODE = "No powerups";
export const ROUND_MODES = [...PLAYSTYLE_NAMES, NO_POWERUPS_MODE];

// The power-up rules a round mode implies.
export function roundModeRules(name) {
    if (name === NO_POWERUPS_MODE) {
        return { disabledPowerUps: ALL_POWERUP_KEYS.slice(), powerUpSpawnChance: 0 };
    }
    return { disabledPowerUps: playstyleDisabledPowerUps(name), powerUpSpawnChance: playstyleSpawnChance(name) };
}

// The next round's mode: any but the one just played. `random` is injectable for tests.
export function nextRoundMode(previous, random = Math.random) {
    const pool = ROUND_MODES.filter((n) => n !== previous);
    return pool[Math.floor(random() * pool.length)];
}
